/**
 * Phase 2 — structural KB vs source comparison (never mutates KB).
 * All eight discrepancy types from the DDX-Plus spec; cant_miss_disagreement is always critical.
 */

/**
 * @param {object} params
 * @param {object[]} params.existingKbNodes - { type: 'edge', id?, diagnosisId, featureId, edgeStrength?, temporalBuckets?, sensitivity?, specificity?, demographicModifiers?, direction? }
 * @param {object} params.sourceContent - { diagnoses?, edges?, sourceName?, sourceUrl?, rawExcerpt?, extractionConfidence?, sourceNegatesCantMiss?, novelEvidence? }
 * @param {string} params.auditTrigger
 * @param {string[]} [params.cantMissDiagnosisIds] - KB can’t-miss dx ids for cant_miss_disagreement checks
 * @returns {object[]}
 */
export function detectDiscrepancies(params) {
  const { existingKbNodes, sourceContent, auditTrigger, cantMissDiagnosisIds } = params;
  const out = [];
  const kb = Array.isArray(existingKbNodes) ? existingKbNodes : [];
  const srcEdges = Array.isArray(sourceContent?.edges) ? sourceContent.edges : [];
  const cantMiss = Array.isArray(cantMissDiagnosisIds) ? cantMissDiagnosisIds.map(String) : [];

  const kbEdgeNodes = kb.filter((n) => n.type === 'edge' && n.featureId && n.diagnosisId);
  const kbEdgeKeys = new Set();
  for (const n of kbEdgeNodes) {
    kbEdgeKeys.add(`${n.diagnosisId}|${n.featureId}`);
  }

  const srcKeysByDx = new Map();
  for (const e of srcEdges) {
    const fid = e.featureId || e.feature_id;
    const did = e.diagnosisId || e.diagnosis_id;
    if (!fid || !did) continue;
    const key = `${did}|${fid}`;
    if (!srcKeysByDx.has(did)) srcKeysByDx.set(did, new Set());
    srcKeysByDx.get(did).add(key);
  }

  const srcKeySet = new Set();
  for (const e of srcEdges) {
    const fid = e.featureId || e.feature_id;
    const did = e.diagnosisId || e.diagnosis_id;
    if (fid && did) srcKeySet.add(`${did}|${fid}`);
  }

  const baseSource = {
    sourceName: sourceContent?.sourceName,
    sourceUrl: sourceContent?.sourceUrl,
    sourceExcerpt: sourceContent?.rawExcerpt?.slice(0, 500) || '',
    confidence: sourceContent?.extractionConfidence ?? 0.5,
  };

  // --- cant_miss_disagreement (always critical; run before other edge checks) ---
  const negates = Array.isArray(sourceContent?.sourceNegatesCantMiss)
    ? sourceContent.sourceNegatesCantMiss.map(String)
    : [];
  for (const cm of cantMiss) {
    if (negates.includes(cm)) {
      out.push({
        discrepancyType: 'cant_miss_disagreement',
        severity: 'critical',
        kbContent: { nodeType: 'diagnosis', nodeId: cm, field: 'cantMiss', currentValue: true },
        sourceContent: { ...baseSource, extractedValue: { negatesCantMiss: cm } },
        explanation: 'Source narrative or metadata explicitly contradicts a KB can’t-miss diagnosis',
        suggestedAction: 'flag_source',
        auditTrigger,
      });
    }
    for (const e of srcEdges) {
      const did = e.diagnosisId || e.diagnosis_id;
      if (String(did) !== cm) continue;
      if (e.direction === 'contradicts' || e.contradictsCantMiss === true) {
        out.push({
          discrepancyType: 'cant_miss_disagreement',
          severity: 'critical',
          kbContent: { nodeType: 'diagnosis', nodeId: cm, field: 'cantMiss', currentValue: true },
          sourceContent: { ...baseSource, extractedValue: e },
          explanation: 'Source edge contradicts a KB can’t-miss diagnosis',
          suggestedAction: 'keep_kb',
          auditTrigger,
        });
      }
    }
  }

  // --- Source edges vs KB ---
  for (const e of srcEdges) {
    const fid = e.featureId || e.feature_id;
    const did = e.diagnosisId || e.diagnosis_id;
    if (!fid || !did) continue;
    const key = `${did}|${fid}`;
    const kbNode = kbEdgeNodes.find((n) => n.diagnosisId === did && n.featureId === fid);

    if (!kbEdgeKeys.has(key)) {
      out.push({
        discrepancyType: 'missing_edge',
        severity: 'moderate',
        kbContent: { nodeType: 'edge', nodeId: null, field: null, currentValue: null },
        sourceContent: { ...baseSource, extractedValue: e },
        explanation: 'Source suggests feature–diagnosis link not present in KB projection set',
        suggestedAction: 'verify_edge_or_reject',
        auditTrigger,
      });
      const novel =
        e.novelEvidence === true ||
        sourceContent?.novelEvidence === true ||
        (e.supportingCitation && String(e.supportingCitation).length > 0);
      if (novel) {
        out.push({
          discrepancyType: 'new_evidence',
          severity: 'moderate',
          kbContent: { nodeType: 'edge', nodeId: null, field: null, currentValue: null },
          sourceContent: { ...baseSource, extractedValue: e },
          explanation: 'Source cites novel evidence for a link absent from KB',
          suggestedAction: 'update_kb',
          auditTrigger,
        });
      }
    } else if (kbNode) {
      const kbStr = kbNode.edgeStrength != null ? Number(kbNode.edgeStrength) : null;
      const srcStr = e.edgeStrength != null ? Number(e.edgeStrength) : null;
      if (kbStr != null && srcStr != null && Math.abs(kbStr - srcStr) >= 0.2) {
        out.push({
          discrepancyType: 'strength_disagreement',
          severity: Math.abs(kbStr - srcStr) >= 0.4 ? 'critical' : 'moderate',
          kbContent: {
            nodeType: 'edge',
            nodeId: kbNode.id,
            field: 'edgeStrength',
            currentValue: kbStr,
          },
          sourceContent: { ...baseSource, extractedValue: srcStr },
          explanation: 'Edge strength differs materially between KB and source extraction',
          suggestedAction: 'update_kb_partially',
          auditTrigger,
        });
      }

      const kbT = kbNode.temporalBuckets;
      const srcT = e.temporalBuckets;
      if (Array.isArray(kbT) && kbT.length > 0 && Array.isArray(srcT) && srcT.length > 0) {
        const norm = (arr) => [...new Set(arr.map(String))].sort().join(',');
        if (norm(kbT) !== norm(srcT)) {
          out.push({
            discrepancyType: 'temporal_disagreement',
            severity: 'moderate',
            kbContent: {
              nodeType: 'edge',
              nodeId: kbNode.id,
              field: 'temporalBuckets',
              currentValue: kbT,
            },
            sourceContent: { ...baseSource, extractedValue: srcT },
            explanation: 'Temporal bucket assignment differs between KB edge and source',
            suggestedAction: 'update_kb_partially',
            auditTrigger,
          });
        }
      }

      const kbSens = kbNode.sensitivity != null ? Number(kbNode.sensitivity) : null;
      const srcSens = e.sensitivity != null ? Number(e.sensitivity) : null;
      const kbSpec = kbNode.specificity != null ? Number(kbNode.specificity) : null;
      const srcSpec = e.specificity != null ? Number(e.specificity) : null;
      const sensDiff =
        kbSens != null &&
        srcSens != null &&
        !Number.isNaN(kbSens) &&
        !Number.isNaN(srcSens) &&
        Math.abs(kbSens - srcSens) >= 0.15;
      const specDiff =
        kbSpec != null &&
        srcSpec != null &&
        !Number.isNaN(kbSpec) &&
        !Number.isNaN(srcSpec) &&
        Math.abs(kbSpec - srcSpec) >= 0.15;
      if (sensDiff || specDiff) {
        out.push({
          discrepancyType: 'sensitivity_specificity_update',
          severity: 'moderate',
          kbContent: {
            nodeType: 'edge',
            nodeId: kbNode.id,
            field: sensDiff ? 'sensitivity' : 'specificity',
            currentValue: sensDiff ? kbSens : kbSpec,
          },
          sourceContent: { ...baseSource, extractedValue: { sensitivity: srcSens, specificity: srcSpec } },
          explanation: 'Sensitivity/specificity estimates differ between KB and source',
          suggestedAction: 'update_kb_partially',
          auditTrigger,
        });
      }

      const kbDm = kbNode.demographicModifiers;
      const srcDm = e.demographicModifiers;
      if (Array.isArray(kbDm) && kbDm.length > 0 && Array.isArray(srcDm) && srcDm.length > 0) {
        try {
          if (JSON.stringify(kbDm) !== JSON.stringify(srcDm)) {
            out.push({
              discrepancyType: 'demographic_modifier',
              severity: 'low',
              kbContent: {
                nodeType: 'edge',
                nodeId: kbNode.id,
                field: 'demographicModifiers',
                currentValue: kbDm,
              },
              sourceContent: { ...baseSource, extractedValue: srcDm },
              explanation: 'Demographic modifier arrays differ between KB and source edge',
              suggestedAction: 'update_kb_partially',
              auditTrigger,
            });
          }
        } catch {
          /* ignore */
        }
      }

      const kbCm = kbNode.contextModifiers;
      const srcCm = e.contextModifiers;
      if (Array.isArray(kbCm) && kbCm.length > 0 && Array.isArray(srcCm) && srcCm.length > 0) {
        try {
          if (JSON.stringify(kbCm) !== JSON.stringify(srcCm)) {
            out.push({
              discrepancyType: 'context_modifier',
              severity: 'low',
              kbContent: {
                nodeType: 'edge',
                nodeId: kbNode.id,
                field: 'contextModifiers',
                currentValue: kbCm,
              },
              sourceContent: { ...baseSource, extractedValue: srcCm },
              explanation: 'Context modifier arrays differ between KB and source edge',
              suggestedAction: 'update_kb_partially',
              auditTrigger,
            });
          }
        } catch {
          /* ignore */
        }
      }
    }
  }

  // --- extra_edge: KB edge for a diagnosis that source also discusses, but source omits this link ---
  for (const kbNode of kbEdgeNodes) {
    const key = `${kbNode.diagnosisId}|${kbNode.featureId}`;
    if (srcKeySet.has(key)) continue;
    const forDx = srcKeysByDx.get(kbNode.diagnosisId);
    if (forDx && forDx.size > 0) {
      out.push({
        discrepancyType: 'extra_edge',
        severity: 'low',
        kbContent: {
          nodeType: 'edge',
          nodeId: kbNode.id,
          field: 'featureId',
          currentValue: kbNode.featureId,
        },
        sourceContent: {
          ...baseSource,
          extractedValue: null,
        },
        explanation:
          'KB includes a feature–diagnosis link for a diagnosis the source also covers, but source extraction omitted this edge',
        suggestedAction: 'keep_kb',
        auditTrigger,
      });
    }
  }

  return out;
}
